<template>
  <div class="bundle-details-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">{{ $t("common.loading") }}</p>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="bundle-content-wrapper">
      <!-- Hero Section -->
      <div class="bundle-hero">
        <div class="hero-background">
          <img
            :src="courseDetails.bundle.imagepath"
            :alt="courseDetails.bundle.title"
            class="hero-image"
            @error="setDefaultImage"
          />
          <div class="hero-overlay"></div>
        </div>
        <div class="hero-content container">
          <div class="hero-badge">
            <i class="fas fa-layer-group"></i>
            <span>{{ $t("bundle.details") }}</span>
          </div>
          <h1 class="hero-title">{{ courseDetails.bundle.title }}</h1>
          <p class="hero-description">{{ courseDetails.bundle.detail }}</p>

          <div class="hero-meta">
            <div class="meta-item">
              <i class="fas fa-book-open"></i>
              <span
                >{{ courseDetails.bundle.course_id.length }}
                {{ $t("bundle.courses_count") }}</span
              >
            </div>
            <div class="meta-item">
              <i class="fas fa-user-tie"></i>
              <span>{{ courseDetails.bundle.user }}</span>
            </div>
            <div class="meta-item">
              <i class="fas fa-calendar-alt"></i>
              <span>{{ formatDate(courseDetails.bundle.updated_at) }}</span>
            </div>
          </div>

          <div class="hero-actions">
            <a
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#share-modal"
              class="share-btn"
            >
              <i class="bi bi-share"></i>
              {{ $t("bundle.share_in_app") }}
            </a>
            <a
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#share-web-modal"
              class="share-btn share-web"
            >
              <i class="bi bi-globe"></i>
              {{ $t("bundle.share_on_web") }}
            </a>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="bundle-main-content container">
        <div class="content-grid">
          <!-- Sidebar -->
          <div class="bundle-sidebar">
            <!-- Price Card with Instructor -->
            <div class="price-card">
              <!-- Instructor Section -->
              <div class="instructor-section">
                <div class="instructor-avatar">
                  <img
                    :src="courseDetails.bundle.user_image_path"
                    :alt="courseDetails.bundle.user"
                    @error="setDefaultImage"
                  />
                </div>
                <div class="instructor-details">
                  <h4 class="instructor-name">
                    {{ courseDetails.bundle.user }}
                  </h4>
                  <span class="instructor-badge">
                    <i class="fas fa-award"></i>
                    {{ $t("bundle.instructor") }}
                  </span>
                </div>
              </div>

              <div class="price-divider"></div>

              <div class="price-header">
                <div class="price-display">
                  <template v-if="courseDetails.bundle.discount_price">
                    <span class="price-current"
                      >{{ courseDetails.bundle.discount_price }}
                      {{ $appCurrency }}</span
                    >
                    <span class="price-old"
                      >{{ courseDetails.bundle.price }} {{ $appCurrency }}</span
                    >
                    <span class="discount-badge">
                      {{
                        calculateDiscountPercentage(
                          courseDetails.bundle.price,
                          courseDetails.bundle.discount_price
                        )
                      }}% {{ $t("bundle.discount") }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="price-current"
                      >{{ courseDetails.bundle.price }} {{ $appCurrency }}</span
                    >
                  </template>
                </div>
              </div>

              <!-- Buy Now Button -->
              <button
                class="buy-now-btn"
                :disabled="buyingNow"
                @click="buyNow(courseDetails.bundle.id)"
              >
                <span
                  v-if="buyingNow"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                <i v-else class="fas fa-shopping-bag"></i>
                {{ $t("common.buy_now") }}
              </button>

              <!-- Add to Cart Button -->
              <button
                class="add-to-cart-btn"
                @click="addToCart(courseDetails.bundle.id, $event, 'bundle')"
              >
                <i class="fas fa-cart-plus"></i>
                {{ $t("bundle.add_to_cart") }}
              </button>
            </div>
          </div>

          <!-- Course Content -->
          <div class="bundle-content-area">
            <!-- Tabs -->
            <div class="bundle-tabs">
              <ul class="nav nav-pills" role="tablist">
                <li class="nav-item">
                  <a
                    class="nav-link"
                    data-bs-toggle="tab"
                    href="#kt_overview_tab"
                  >
                    <i class="fas fa-info-circle"></i>
                    {{ $t("bundle.overview") }}
                  </a>
                </li>
                <li class="nav-item">
                  <a
                    class="nav-link active"
                    data-bs-toggle="tab"
                    href="#kt_user_view_overview_tab"
                  >
                    <i class="fas fa-list-ul"></i>
                    {{ $t("bundle.bundle_content") }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Tab Content -->
            <div class="tab-content">
              <!-- Overview Tab -->
              <div class="tab-pane fade" id="kt_overview_tab" role="tabpanel">
                <div class="overview-card">
                  <h3 class="section-title">
                    <i class="fas fa-chart-bar"></i>
                    {{ $t("bundle.bundle_stats") || "Bundle Statistics" }}
                  </h3>
                  <div class="stats-grid">
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-book-open"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value">{{
                          courseDetails.bundle.course_id.length
                        }}</span>
                        <span class="stat-label">{{
                          $t("bundle.courses_count")
                        }}</span>
                      </div>
                    </div>
                    <div class="stat-item">
                      <div class="stat-icon">
                        <i class="fas fa-user-graduate"></i>
                      </div>
                      <div class="stat-info">
                        <span class="stat-value">{{
                          courseDetails.bundle.user
                        }}</span>
                        <span class="stat-label">{{
                          $t("bundle.instructor")
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="bundle-description"
                    v-if="courseDetails.bundle.detail"
                  >
                    <h4>{{ $t("bundle.about") || "About this Bundle" }}</h4>
                    <p>{{ courseDetails.bundle.detail }}</p>
                  </div>
                </div>
              </div>

              <!-- Bundle Content Tab -->
              <div
                class="tab-pane fade show active"
                id="kt_user_view_overview_tab"
                role="tabpanel"
              >
                <div class="courses-section">
                  <div class="section-header">
                    <h3 class="section-title">
                      <i class="fas fa-graduation-cap"></i>
                      {{ $t("bundle.included_courses") || "Included Courses" }}
                    </h3>
                    <span class="courses-count"
                      >{{ courseDetails.bundle.course.length }}
                      {{ $t("bundle.courses_count") }}</span
                    >
                  </div>

                  <!-- Empty State -->
                  <div
                    v-if="courseDetails.bundle.course.length === 0"
                    class="empty-state"
                  >
                    <div class="empty-icon">
                      <i class="fas fa-box-open"></i>
                    </div>
                    <h4>{{ $t("bundle.no_courses") }}</h4>
                  </div>

                  <!-- Courses Grid -->
                  <div v-else class="courses-grid">
                    <div
                      v-for="course in courseDetails.bundle.course"
                      :key="course.id"
                      class="course-card"
                    >
                      <div class="course-image-wrapper">
                        <img
                          :src="course.img_path"
                          :alt="course.title"
                          class="course-image"
                          @error="setDefaultImage"
                        />
                        <div class="course-overlay">
                          <a
                            :href="`/course-details?id=${course.id}`"
                            class="view-course-btn"
                          >
                            <i class="fas fa-play"></i>
                          </a>
                        </div>
                      </div>
                      <div class="course-content">
                        <h5 class="course-title">
                          <a :href="`/course-details?id=${course.id}`">{{
                            course.title
                          }}</a>
                        </h5>
                        <div class="course-price">
                          <span class="price"
                            >{{ course.price || "—" }} {{ $appCurrency }}</span
                          >
                        </div>
                        <button
                          class="course-cart-btn"
                          :class="{ 'in-cart': isInCart(course.id) }"
                          :disabled="loadingCourseCart[course.id]"
                          @click="toggleCourseCart(course.id, $event)"
                        >
                          <i
                            v-if="loadingCourseCart[course.id]"
                            class="fas fa-spinner fa-spin"
                          ></i>
                          <i
                            v-else-if="isInCart(course.id)"
                            class="fas fa-times"
                          ></i>
                          <i v-else class="fas fa-cart-plus"></i>
                          <span v-if="!loadingCourseCart[course.id]">{{
                            isInCart(course.id)
                              ? $t("bundle.remove_from_cart") ||
                                "إزالة من السلة"
                              : $t("bundle.add_to_cart") || "أضف للسلة"
                          }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Bottom Buy Bar (Mobile) -->
      <div class="fixed-buy-bar">
        <div class="buy-bar-content">
          <div class="buy-bar-price">
            <template v-if="courseDetails.bundle.discount_price">
              <span class="current-price"
                >{{ courseDetails.bundle.discount_price }}
                {{ $appCurrency }}</span
              >
              <span class="old-price"
                >{{ courseDetails.bundle.price }} {{ $appCurrency }}</span
              >
            </template>
            <template v-else>
              <span class="current-price"
                >{{ courseDetails.bundle.price }} {{ $appCurrency }}</span
              >
            </template>
          </div>
          <button class="buy-bar-btn" @click="buyNow(courseDetails.bundle.id)">
            <i class="fas fa-shopping-bag"></i>
            {{ $t("common.buy_now") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <ShareModal />
</template>

<script>
import { baseUrl, apiUrl } from "@/utils/api";
import { showCart, addToCart, removeCartItem } from "@/utils/cart";
import ShareModal from "./share.vue";
import Swal from "sweetalert2";
import i18n from "@/plugins/i18n";

export default {
  name: "BundleDetails",
  components: {
    ShareModal,
  },
  data() {
    return {
      token: localStorage.getItem("access_token"),
      baseUrl: baseUrl,
      courseId: this.$route.query.id,
      defaultImage: "/assets/img/acad-icon.png",
      cartItems: [],
      cartCourseIds: [],
      loadingCourseCart: {},
      courseDetails: {
        bundle: {
          category: "",
          user: "",
          user_image: "",
          title: "",
          detail: "",
          price: "",
          discount_price: "",
          course_id: [],
          course: [],
          imagepath: "",
        },
      },
      loading: true,
      buyingNow: false,
    };
  },
  mounted() {
    this.fetchCourseDetails();
    this.loadCartItems();
  },
  methods: {
    calculateDiscountPercentage(price, discountPrice) {
      if (price > 0) {
        return Math.round(((price - discountPrice) / price) * 100);
      }
      return 0;
    },
    setDefaultImage(event) {
      event.target.src = this.defaultImage;
    },
    async loadCartItems() {
      if (!this.token) return;
      try {
        const response = await fetch(
          `${apiUrl}/show/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        const data = await response.json();
        if (data.cart && Array.isArray(data.cart)) {
          this.cartCourseIds = data.cart
            .filter((item) => item.course_id !== null)
            .map((item) => item.course_id);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    },
    isInCart(courseId) {
      return this.cartCourseIds.includes(courseId);
    },
    async toggleCourseCart(courseId, event) {
      if (!this.token) {
        Swal.fire({
          icon: "warning",
          title: this.$t("common.login_required") || "يرجى تسجيل الدخول",
          confirmButtonText: this.$t("common.ok") || "حسناً",
          confirmButtonColor: "#00c744",
        });
        return;
      }

      this.loadingCourseCart[courseId] = true;

      if (this.isInCart(courseId)) {
        // Remove from cart
        try {
          const response = await fetch(
            `${apiUrl}/remove/cart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-country-code": window.appRegion?.phone_code || "",
                Authorization: `Bearer ${this.token}`,
              },
              body: JSON.stringify({ course_id: courseId }),
            }
          );
          if (response.ok) {
            this.cartCourseIds = this.cartCourseIds.filter(
              (id) => id !== courseId
            );
            Swal.fire({
              icon: "success",
              text: this.$t("bundle.course_removed") || "تمت الإزالة من السلة",
              showConfirmButton: false,
              timer: 1500,
            });
          }
        } catch (error) {
          console.error("Error removing from cart:", error);
          Swal.fire({
            icon: "error",
            text: this.$t("bundle.cart_remove_error") || "فشل في الإزالة",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      } else {
        // Add to cart
        try {
          const response = await fetch(
            `${apiUrl}/addtocart?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-country-code": window.appRegion?.phone_code || "",
                Authorization: `Bearer ${this.token}`,
              },
              body: JSON.stringify({ course_id: courseId }),
            }
          );
          if (response.ok) {
            this.cartCourseIds.push(courseId);
            Swal.fire({
              icon: "success",
              text: this.$t("bundle.course_added") || "تمت الإضافة للسلة",
              showConfirmButton: false,
              timer: 1500,
            });
          }
        } catch (error) {
          console.error("Error adding to cart:", error);
          Swal.fire({
            icon: "error",
            text: this.$t("bundle.cart_add_error") || "فشل في الإضافة",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      }

      this.loadingCourseCart[courseId] = false;
    },
    async buyNow(bundleId) {
      this.buyingNow = true;
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${apiUrl}/addtocart/bundle?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
            body: `bundle_id=${bundleId}`,
          }
        );
        if (response.ok) {
          this.$router.push("/cart");
        } else {
          console.error("Failed to add bundle to cart");
          this.$router.push("/cart");
        }
      } catch (error) {
        console.error("Error adding bundle to cart:", error);
        this.$router.push("/cart");
      } finally {
        this.buyingNow = false;
      }
    },
    async fetchCourseDetails() {
      try {
        const response = await fetch(
          `${apiUrl}/bundle/courses/v2/${this.courseId}?secret=a463c0cd-78ed-4bda-a5f6-f70c3e597dfb`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-country-code": window.appRegion.phone_code,
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch course details");
        }
        const responseData = await response.json();
        this.courseDetails = responseData;

        const dynamicBaseUrl = window.location.origin;
        let shareModal = document.getElementById("share-content");

        shareModal.innerHTML = `
  <div style="text-align:right">
      ${i18n.global.t("share.app_prefix")}<br><br>
      ${responseData.bundle.title}<br><br>
      <a href="https://instructor.academia.ly/bundle/?id=${
        responseData.bundle.id
      }" target="_blank">https://instructor.academia.ly/bundle/?id=${
          responseData.bundle.id
        }</a><br><br>
      ${i18n.global.t("share.contact_telegram")}
  </div>
  <div id="copyButton" class="mt-3 btn-sm btn btn-success">${i18n.global.t(
    "share.copy_button"
  )} <i class="fa fa-copy"></i></div>
`;

        document
          .getElementById("copyButton")
          .addEventListener("click", async function () {
            const textToCopy = `${i18n.global.t("share.app_prefix")}\n\n${
              responseData.bundle.title
            }\n\nhttps://instructor.academia.ly/bundle/?id=${
              responseData.bundle.id
            }\n\n${i18n.global.t("share.contact_telegram")}`;

            try {
              await navigator.clipboard.writeText(textToCopy);
              Swal.fire({
                icon: "success",
                title: i18n.global.t("share.copy_success_title"),
                text: i18n.global.t("share.copy_success_text"),
                confirmButtonText: i18n.global.t("common.ok"),
              });
            } catch (err) {
              console.error("Copy failed:", err);
              alert(i18n.global.t("share.copy_error"));
            }
          });

        let shareWebModal = document.getElementById("share-web-content");

        shareWebModal.innerHTML = `
        <div style="text-align:right">
            ${i18n.global.t("share.app_prefix")}<br><br>
            ${responseData.bundle.title}<br><br>
            <a href="${dynamicBaseUrl}/bundle-details?id=${
          responseData.bundle.id
        }" target="_blank">${dynamicBaseUrl}/bundle-details?id=${
          responseData.bundle.id
        }</a><br><br>
            ${i18n.global.t("share.contact_telegram")}
        </div>
        <div id="copyButtonWeb" class="mt-3 btn-sm btn btn-success">${i18n.global.t(
          "share.copy_button"
        )} <i class="fa fa-copy"></i></div>
      `;

        document
          .getElementById("copyButtonWeb")
          .addEventListener("click", async function () {
            const textToCopy = `${i18n.global.t("share.app_prefix")}\n\n${
              responseData.bundle.title
            }\n\n${dynamicBaseUrl}/bundle-details?id=${
              responseData.bundle.id
            }\n\n${i18n.global.t("share.contact_telegram")}`;

            try {
              await navigator.clipboard.writeText(textToCopy);
              Swal.fire({
                icon: "success",
                title: i18n.global.t("share.copy_success_title"),
                text: i18n.global.t("share.copy_success_text"),
                confirmButtonText: i18n.global.t("common.ok"),
              });
            } catch (err) {
              console.error("Copy failed:", err);
              alert(i18n.global.t("share.copy_error"));
            }
          });

        this.loading = false;
      } catch (error) {
        console.error("Error fetching course details:", error);
        this.loading = false;
      }
    },
    addToCart,
    removeCartItem,
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const loc = i18n.global.locale.value === "ar" ? "ar-EG" : "en-US";
      return d.toLocaleDateString(loc, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
/* ===== Main Page Styles ===== */
.bundle-details-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  direction: rtl;
}

/* ===== Loading State ===== */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid transparent;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  animation-delay: -0.45s;
}
.spinner-ring:nth-child(2) {
  animation-delay: -0.3s;
  opacity: 0.8;
}
.spinner-ring:nth-child(3) {
  animation-delay: -0.15s;
  opacity: 0.6;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #00c744;
  font-size: 1.1rem;
  font-weight: 600;
}

/* ===== Hero Section ===== */
.bundle-hero {
  position: relative;
  min-height: 400px;
  overflow: hidden;
  margin-bottom: 40px;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 50, 20, 0.85) 0%,
    rgba(0, 100, 40, 0.75) 100%
  );
}

.hero-content {
  position: relative;
  z-index: 2;
  padding: 60px 20px;
  color: white;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 20px;
}

.hero-badge i {
  font-size: 1rem;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 15px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.hero-description {
  font-size: 1.1rem;
  opacity: 0.9;
  max-width: 700px;
  line-height: 1.7;
  margin-bottom: 25px;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 25px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  padding: 10px 18px;
  border-radius: 25px;
  font-size: 0.95rem;
  backdrop-filter: blur(5px);
}

.meta-item i {
  font-size: 1rem;
  color: #7fff9b;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.share-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  color: white;
}

.share-btn.share-web {
  background: #00c744;
  border-color: #00c744;
}

.share-btn.share-web:hover {
  background: #00a83b;
}

/* ===== Main Content Grid ===== */
.bundle-main-content {
  padding: 0 20px 100px;
}

.content-grid {
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 40px;
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* ===== Sidebar ===== */
.bundle-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Price Card */
.price-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 199, 68, 0.1);
  border: 1px solid rgba(0, 199, 68, 0.1);
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

/* Instructor Section inside Price Card */
.instructor-section {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.instructor-section .instructor-avatar {
  width: 65px;
  height: 65px;
  min-width: 65px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #00c744;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.2);
}

.instructor-section .instructor-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.instructor-section .instructor-details {
  flex: 1;
}

.instructor-section .instructor-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 5px 0;
}

.instructor-section .instructor-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #e8fff1, #d4f5e4);
  color: #00a83b;
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
}

.price-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
  margin-bottom: 20px;
}

.price-header {
  margin-bottom: 25px;
  text-align: center;
}

.price-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.price-current {
  font-size: 2.5rem;
  font-weight: 800;
  color: #00c744;
}

.price-old {
  font-size: 1.2rem;
  color: #999;
  text-decoration: line-through;
}

.discount-badge {
  background: linear-gradient(135deg, #ff6b6b, #ee5a5a);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
}

.buy-now-btn {
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
}

.buy-now-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
}

.buy-now-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.add-to-cart-btn {
  width: 100%;
  padding: 14px 24px;
  background: white;
  color: #00c744;
  border: 2px solid #00c744;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.add-to-cart-btn:hover {
  background: #f0fff5;
}

.price-features {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  color: #555;
  font-size: 0.95rem;
}

.feature-item i {
  color: #00c744;
  font-size: 1rem;
}

/* ===== Content Area ===== */
.bundle-content-area {
  min-width: 0;
}

/* Tabs */
.bundle-tabs {
  margin-bottom: 30px;
}

.bundle-tabs .nav-pills {
  background: white;
  padding: 8px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  display: inline-flex;
  gap: 8px;
}

.bundle-tabs .nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  color: #666;
  font-weight: 600;
  transition: all 0.3s ease;
}

.bundle-tabs .nav-link:hover {
  background: #f0fff5;
  color: #00c744;
}

.bundle-tabs .nav-link.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
}

/* Overview Card */
.overview-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 25px;
}

.section-title i {
  color: #00c744;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
  border-radius: 15px;
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon i {
  font-size: 1.5rem;
  color: white;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #333;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
}

.bundle-description {
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.bundle-description h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.bundle-description p {
  color: #555;
  line-height: 1.8;
}

/* Courses Section */
.courses-section {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
}

.courses-count {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.empty-icon i {
  font-size: 2.5rem;
  color: #ff9800;
}

.empty-state h4 {
  color: #666;
  font-weight: 600;
}

/* Courses Grid */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 25px;
}

.course-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.course-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 199, 68, 0.15);
}

.course-image-wrapper {
  position: relative;
  height: 160px;
  overflow: hidden;
}

.course-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.course-card:hover .course-image {
  transform: scale(1.05);
}

.course-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.course-card:hover .course-overlay {
  opacity: 1;
}

.view-course-btn {
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00c744;
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.view-course-btn:hover {
  transform: scale(1.1);
  color: #00c744;
}

/* Cart Toggle Button */
.cart-toggle-btn {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  z-index: 5;
}

.cart-toggle-btn i {
  font-size: 1rem;
  color: #666;
  transition: all 0.3s ease;
}

.cart-toggle-btn:hover {
  transform: scale(1.1);
}

.cart-toggle-btn.in-cart {
  background: #00c744;
}

.cart-toggle-btn.in-cart i {
  color: white;
}

.course-content {
  padding: 20px;
}

.course-title {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 10px;
  line-height: 1.4;
}

.course-title a {
  color: #333;
  text-decoration: none;
  transition: color 0.3s ease;
}

.course-title a:hover {
  color: #00c744;
}

.course-price {
  margin-bottom: 15px;
}

.course-price .price {
  font-size: 1.2rem;
  font-weight: 700;
  color: #00c744;
}

.course-cart-btn {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 2px solid #00c744;
  background: white;
  color: #00c744;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.course-cart-btn:hover {
  background: #f0fff5;
}

.course-cart-btn.in-cart {
  background: #00c744;
  color: white;
}

.course-cart-btn.in-cart:hover {
  background: #00a83b;
}

/* ===== Fixed Bottom Buy Bar ===== */
.fixed-buy-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 15px 20px;
  box-shadow: 0 -5px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: none;
}

@media (max-width: 1024px) {
  .fixed-buy-bar {
    display: block;
  }

  .price-card {
    position: static;
  }
}

.buy-bar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  gap: 20px;
}

.buy-bar-price {
  display: flex;
  align-items: center;
  gap: 12px;
}

.buy-bar-price .current-price {
  font-size: 1.5rem;
  font-weight: 800;
  color: #00c744;
}

.buy-bar-price .old-price {
  font-size: 1rem;
  color: #999;
  text-decoration: line-through;
}

.buy-bar-btn {
  padding: 14px 30px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.buy-bar-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(0, 199, 68, 0.4);
}

/* ===== Responsive Design ===== */
@media (max-width: 768px) {
  .hero-title {
    font-size: 1.8rem;
  }

  .hero-description {
    font-size: 1rem;
  }

  .hero-meta {
    gap: 10px;
  }

  .meta-item {
    padding: 8px 14px;
    font-size: 0.85rem;
  }

  .bundle-main-content {
    padding: 0 15px 120px;
  }

  .courses-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
  }

  .price-current {
    font-size: 2rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-content {
    padding: 40px 15px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-actions {
    flex-direction: column;
  }

  .share-btn {
    width: 100%;
    justify-content: center;
  }

  .courses-grid {
    grid-template-columns: 1fr;
  }

  .bundle-tabs .nav-pills {
    width: 100%;
    flex-direction: column;
  }

  .bundle-tabs .nav-link {
    justify-content: center;
  }
}
</style>
